<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" sm="10" md="8" lg="7">
        <v-card>
          <v-card-title>
            Profile
            <v-spacer />
            <v-btn
              v-if="!editingProfile"
              color="primary"
              @click="editingProfile = true"
              ><v-icon small left>mdi-pencil</v-icon>Edit</v-btn
            >
          </v-card-title>
          <v-card-text>
            <v-row justify="center" no-gutters>
              <v-col cols="6" sm="4" class="text-center">
                <v-avatar size="100">
                  <ProfileImage size="100" />
                </v-avatar>
                <v-row v-if="editingProfile">
                  <v-col>
                    <v-btn
                      color="primary"
                      :loading="profileImage.isSelecting"
                      @click="onButtonClick"
                      rounded
                    >
                      <v-icon left>mdi-cloud-upload</v-icon>
                      Upload
                      <input
                        ref="uploader"
                        class="d-none"
                        type="file"
                        accept="image/*"
                        @change="onFileChanged"
                      />
                    </v-btn>
                    <v-snackbar v-model="profileImage.showError">
                      {{ profileImage.errorMessage }}
                      <v-btn
                        text
                        @click="profileImage.showError = false"
                        color="error"
                        >Close</v-btn
                      >
                    </v-snackbar>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" sm="6">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-form
                      @submit.prevent="updateProfile"
                      ref="profileForm"
                      v-model="profileForm.valid"
                    >
                      <p class="overline mb-2">Name</p>
                      <v-text-field
                        v-if="editingProfile"
                        :value="name"
                        outlined
                        ref="name"
                        :rules="[rules.required]"
                        dense
                      ></v-text-field>
                      <p v-if="!editingProfile" class="body-1">
                        {{ name }}
                      </p>
                      <v-card-actions v-if="editingProfile">
                        <v-spacer />
                        <v-btn color="error" @click="editingProfile = false"
                          >Cancel</v-btn
                        >
                        <v-btn
                          color="info"
                          type="submit"
                          @click="profileFormValidate"
                          :loading="profileForm.loading"
                          :disabled="profileImage.isUploading"
                          >Save</v-btn
                        >
                      </v-card-actions>

                      <v-snackbar v-model="profileForm.showSuccess"
                        >Successfully updated profile!
                        <v-btn
                          text
                          @click="profileForm.showSuccess = false"
                          color="error"
                          >Close</v-btn
                        >
                      </v-snackbar>
                    </v-form>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <p class="overline mb-2">
                      Cubes
                      <v-tooltip right max-width="300px">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            class="pl-1"
                            v-on="on"
                            v-bind="attrs"
                            size="medium"
                            style="position: absolute"
                          >
                            mdi-help-circle-outline
                          </v-icon>
                        </template>
                        <span class="text-wrap"
                          >Cubes are a simple point system designed to reward
                          hard work and promote productivity</span
                        >
                      </v-tooltip>
                    </p>
                    <span class="body-1">{{ cubeCount }}</span>
                    <v-icon right size="20">mdi-cube-outline</v-icon>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card> </v-col
      ><v-col cols="12" sm="10" md="8" lg="7">
        <v-card class="fill-height">
          <v-card-title> Account Info </v-card-title>
          <v-card-text>
            <v-alert
              type="success"
              dense
              dismissible
              v-if="passwordForm.showSuccess"
              >Password was updated!</v-alert
            >
            <v-alert
              type="success"
              dense
              dismissible
              v-if="emailForm.showSuccess"
              >Email updated!</v-alert
            >
            <v-row justify="center" align="center" class="mx-2">
              <v-col cols="12" sm="6">
                <p class="overline">Email</p>
                <v-text-field
                  :value="email"
                  outlined
                  dense
                  readonly
                  append-icon="mdi-pencil"
                  @click:append="editingEmail = true"
                >
                </v-text-field>
                <ChangeEmailDialog
                  :editing-email="editingEmail"
                  :email="email"
                  @updateEmail="updateEmail"
                  @onDialogClosed="editingEmail = false"
                  :showError="emailForm.showError"
                  :errorMessage="emailForm.errorMessage"
                ></ChangeEmailDialog>
              </v-col>
              <v-col cols="12" sm="6">
                <p class="overline">Password</p>
                <v-text-field
                  type="password"
                  readonly
                  dense
                  :value="'************'"
                  append-icon="mdi-pencil"
                  outlined
                  @click:append="editingPassword = true"
                ></v-text-field>
                <ChangePasswordDialog
                  :editing-password="editingPassword"
                  @updatePassword="updatePassword"
                  @onDialogClosed="editingPassword = false"
                  :showError="passwordForm.showError"
                  :errorMessage="passwordForm.errorMessage"
                ></ChangePasswordDialog>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="10" md="8" lg="7">
        <v-card class="pb-8">
          <v-card-title> Customization </v-card-title>
          <v-card-text>
            <v-row justify="center">
              <v-col cols="12" sm="10" md="8" lg="6">
                <p class="overline">Favorite Color</p>
                <ColorTiles
                  :favorite-color="favoriteColor"
                  @onColorSelected="setFavoriteColor"
                />
              </v-col>
            </v-row>
            <v-row justify="center">
              <v-col cols="12" sm="10" md="8" lg="6">
                <p class="overline">Sound settings</p>
                <v-switch
                  :input-value="soundsEnabled"
                  @change="setSoundsEnabled"
                  label="Sounds Enabled"
                ></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="10" md="8" lg="7">
        <v-card style="border: 1px solid #ff5252">
          <v-card-title>Advanced Options</v-card-title>
          <v-divider />
          <v-row justify="center" no-gutters>
            <v-col cols="12">
              <v-card tile class="elevation-0">
                <v-row justify="center" align="center" no-gutters>
                  <v-col cols="12" sm="8">
                    <v-card-title>Delete your account</v-card-title>
                    <v-card-text>
                      This action will delete your account and all its data.
                      This option cannot be undone.</v-card-text
                    >
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-card-actions>
                      <v-spacer />
                      <v-dialog v-model="deleteDialog" width="500">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn v-on="on" v-bind="attrs" color="error" outlined
                            >Delete account</v-btn
                          >
                        </template>
                        <v-card>
                          <v-card-title>Delete account?</v-card-title>
                          <v-card-text>
                            This will permanently delete your account and all of
                            its data.
                          </v-card-text>
                          <v-card-actions>
                            <v-spacer />
                            <v-btn
                              color="info"
                              text
                              @click="deleteDialog = false"
                              >Cancel</v-btn
                            >
                            <v-btn text color="info" @click="deleteAccount"
                              >Delete</v-btn
                            >
                          </v-card-actions>
                        </v-card>
                      </v-dialog>
                    </v-card-actions>
                  </v-col>
                </v-row>
              </v-card>
              <v-divider />
              <v-card tile class="elevation-0">
                <v-row justify="center" align="center" no-gutters>
                  <v-col cols="12" sm="8">
                    <v-card-title>Account Reset</v-card-title>
                    <v-card-text>
                      You can choose to reset various account properties such
                      as: preferences, cubes, or todos.
                    </v-card-text>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-card-actions>
                      <v-spacer />
                      <v-dialog
                        v-model="reset.dialog"
                        width="500"
                        @click:outside="cancelReset"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn outlined v-on="on" v-bind="attrs"
                            >View Options</v-btn
                          >
                        </template>
                        <v-card>
                          <v-form ref="resetForm">
                            <v-card-title>Reset Options</v-card-title>
                            <v-card-text>
                              <v-list>
                                <v-list-item>
                                  <v-checkbox
                                    @change="resetOptionChange"
                                    id="preferences"
                                    persistent-hint
                                    hint="Resets user images, and theme preferences"
                                    label="Preferences"
                                  ></v-checkbox>
                                </v-list-item>
                                <v-list-item>
                                  <v-checkbox
                                    @change="resetOptionChange"
                                    id="cubes"
                                    persistent-hint
                                    hint="Sets your cube count to 0"
                                    label="Cubes"
                                  ></v-checkbox>
                                </v-list-item>
                                <v-list-item>
                                  <v-checkbox
                                    @change="resetOptionChange"
                                    id="todos"
                                    persistent-hint
                                    hint="Will clear all todos from all lists"
                                    label="To-Do's"
                                  ></v-checkbox>
                                </v-list-item>
                              </v-list>
                            </v-card-text>
                            <v-card-actions>
                              <v-spacer />
                              <v-btn text @click="cancelReset" color="primary"
                                >Cancel</v-btn
                              >
                              <v-btn
                                text
                                :disabled="reset.selectedOptions.length === 0"
                                @click="resetOptions"
                                color="primary"
                                >Confirm</v-btn
                              >
                            </v-card-actions>
                          </v-form>
                        </v-card>
                      </v-dialog>
                    </v-card-actions>
                  </v-col>
                </v-row>
              </v-card>
              <v-divider />
              <v-card tile class="elevation-0">
                <v-row justify="center" align="center" no-gutters>
                  <v-col cols="12" sm="8">
                    <v-card-title>Request Data</v-card-title>

                    <v-card-text>
                      You may request your account data if you wish, please use
                      this option only when necessary.
                    </v-card-text>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-card-actions>
                      <v-spacer />
                      <v-dialog v-model="dataRequest.dialog" width="500">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn outlined v-on="on" v-bind="attrs"
                            >Show Form</v-btn
                          >
                        </template>
                        <v-card>
                          <v-card-title>Request Form </v-card-title>
                          <v-card-subtitle>
                            Here you can request to download all or part of your
                            account data.
                          </v-card-subtitle>
                          <v-card-text>
                            <v-form>
                              <v-row>
                                <v-col cols="12" sm="6">
                                  <v-select
                                    label="Options"
                                    :items="dataRequest.options"
                                    v-model="dataRequest.selectedOption"
                                  ></v-select>
                                </v-col>
                                <v-col cols="12" sm="6">
                                  <v-text-field
                                    v-model="dataRequest.explanation"
                                    label="Brief explanation"
                                  >
                                  </v-text-field>
                                </v-col>
                                <v-col cols="12" sm="6">
                                  <v-text-field
                                    label="File Name"
                                    v-model="dataRequest.fileName"
                                  ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="6">
                                  <v-select
                                    label="Format"
                                    :items="dataRequest.format"
                                    v-model="dataRequest.selectedFormat"
                                    persistent-hint
                                    hint="Other options available soon"
                                  >
                                  </v-select>
                                </v-col>
                              </v-row>
                            </v-form>
                          </v-card-text>
                          <v-card-actions>
                            <v-spacer />
                            <v-btn
                              color="info"
                              text
                              @click="dataRequest.dialog = false"
                              >Cancel</v-btn
                            >
                            <v-btn text color="info" @click="requestData"
                              >Download</v-btn
                            >
                          </v-card-actions>
                        </v-card>
                      </v-dialog>
                    </v-card-actions>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import ProfileImage from '@/components/ProfileImage'
import ChangePasswordDialog from '@/components/ChangePasswordDialog'
import ChangeEmailDialog from '@/components/ChangeEmailDialog'
import ColorTiles from '@/components/ColorTiles'

const firebase = require('../firebaseConfig')
export default {
  name: 'Settings',
  components: {
    ColorTiles,
    ChangeEmailDialog,
    ChangePasswordDialog,
    ProfileImage,
  },
  computed: {
    ...mapGetters([
      'email',
      'name',
      'title',
      'uid',
      'emailIsVerified',
      'favoriteColor',
      'profileImageUrl',
      'darkMode',
      'cubeCount',
      'soundsEnabled',
    ]),
  },
  data: () => ({
    editingProfile: false,
    editingEmail: false,
    editingPassword: false,
    profileForm: {
      valid: false,
      errorMessage: '',
      showError: false,
      showSuccess: false,
      loading: false,
    },
    passwordForm: {
      showSuccess: false,
      errorMessage: '',
      showError: false,
    },
    emailForm: {
      showSuccess: false,
      errorMessage: '',
      showError: false,
    },
    profileImage: {
      file: null,
      isSelecting: false,
      errorMessage: '',
      showError: false,
      showSuccess: false,
      isUploading: false,
    },
    rules: {
      required: (value) => !!value || 'Required.',
    },
    deleteDialog: false,
    resetDialog: false,
    reset: {
      dialog: false,
      selectedOptions: [],
    },
    dataRequest: {
      fileName: 'userData',
      dialog: false,
      options: ['Preferences Only', "To-Do's only", 'All User Data'],
      format: [
        {
          text: '.csv',
          disabled: true,
        },
        {
          text: '.txt',
          disabled: true,
        },
        {
          text: '.json',
          disabled: false,
        },
      ],
      selectedFormat: '.json',
      selectedOption: 'Preferences Only',
      explanation: null,
    },
  }),
  methods: {
    async setSoundsEnabled() {
      return firebase.usersCollection
        .doc(this.uid)
        .update({ soundsEnabled: !this.soundsEnabled })
        .then(() => {
          this.$store.dispatch('fetchUserProfile')
        })
    },
    requestData() {
      let data = this.dataRequest
      data.dialog = false
      let userData = {}

      let preferences = this.$store.state.userProfile
      let todos = this.$store.state.todos
      if (data.selectedOption === data.options[0]) {
        userData = preferences
      } else if (data.selectedOption === data.options[1]) {
        userData = todos
      } else {
        userData = { preferences, todos }
      }
      let blob = new Blob([JSON.stringify(userData)], {
        type: 'application/json',
      })
      let link = document.createElement('a')
      if (link.download !== undefined) {
        // feature detection
        // Browsers that support HTML5 download attribute
        let url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', data.fileName + data.selectedFormat)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    getUserTodos() {},
    cancelReset() {
      this.$refs.resetForm.reset()
      this.reset.dialog = false
    },
    async resetOption(optionToReset) {
      let userInfo = firebase.usersCollection.doc(this.uid)
      if (optionToReset === 'preferences') {
        if (this.favoriteColor !== '#2196f3') {
          userInfo.update('favoriteColor', '#2196f3')
        }
        if (this.profileImageUrl !== null) {
          userInfo.update('profileImageUrl', null)
        }
        if (this.darkMode) {
          userInfo.update('darkMode', false)
        }
      } else if (optionToReset === 'cubes') {
        userInfo.update('cubeCount', 0)
      } else if (optionToReset === 'todos') {
        const todos = await userInfo.collection('todos').get()
        const batch = firebase.db.batch()
        todos.forEach((doc) => batch.delete(doc.ref))
        await batch.commit()
      }
    },
    resetOptions() {
      let options = this.reset.selectedOptions
      options.forEach((option) => this.resetOption(option))
      this.reset.dialog = false
      this.$store.dispatch('fetchUserProfile')
    },
    resetOptionChange() {
      this.$refs.resetForm.inputs.forEach((el) => {
        let options = this.reset.selectedOptions
        let option = el.id
        let selected = el.lazyValue
        let inSelected = options.includes(el.id)
        if (inSelected) {
          let idx = options.indexOf(option)
          options.splice(idx, 1)
        }
        if (selected) {
          options.push(option)
        }
      })
    },
    async deleteAccount() {
      const user = firebase.auth.currentUser
      const userDoc = firebase.usersCollection.doc(this.uid)
      const todos = await userDoc.collection('todos').get()
      const batch = firebase.db.batch()

      todos.forEach((doc) => batch.delete(doc.ref))
      batch.delete(userDoc)
      await firebase.storage
        .ref()
        .child(`profile_images/${this.uid}`)
        .delete()
        .catch(() => {})
      await batch.commit()
      await user.delete()
      this.$router.push('/')
    },
    updateEmail(newEmail) {
      firebase.auth.currentUser
        .updateEmail(newEmail)
        .then(() => {
          this.editingEmail = false
          this.emailForm.showSuccess = true
        })
        .catch((err) => {
          this.emailForm.errorMessage = err.message
          this.emailForm.showError = true
        })
    },
    updateProfile() {
      const isValid = this.$refs.profileForm.validate()
      if (!isValid) {
        this.profileForm.loading = false
        return
      }
      const name = this.$refs.name.lazyValue
      return this.setProfileImage()
        .then(() => firebase.usersCollection.doc(this.uid).update({ name }))
        .then(() => this.$store.dispatch('fetchUserProfile'))
        .then(() => {
          this.editingProfile = false
          this.profileForm.showSuccess = true
        })
        .catch((err) => {
          this.profileForm.errorMessage = err.message
          this.profileForm.showError = true
        })
        .finally(() => {
          this.profileForm.loading = false
        })
    },
    updatePassword({ currentPassword, newPassword }) {
      const user = firebase.auth.currentUser
      const credential = firebase.firebase.auth.EmailAuthProvider.credential(
        this.email,
        currentPassword
      )
      user
        .reauthenticateWithCredential(credential)
        .then(() => user.updatePassword(newPassword))
        .then(() => {
          this.editingPassword = false
          this.passwordForm.showSuccess = true
        })
        .catch((err) => {
          this.passwordForm.errorMessage = err.message
          this.passwordForm.showError = true
        })
    },

    profileFormValidate() {
      this.profileForm.loading = true
      this.profileImage.showError = false
      this.profileImage.errorMessage = ''
      this.$refs.profileForm.validate()
    },

    async setFavoriteColor(color) {
      return firebase.usersCollection
        .doc(this.uid)
        .update('favoriteColor', color)
        .then(() => {
          this.$store.dispatch('fetchUserProfile')
        })
        .catch((err) => console.log(err))
    },

    onButtonClick() {
      this.profileImage.isUploading = true
      this.profileImage.isSelecting = true
      window.addEventListener(
        'focus',
        () => {
          this.profileImage.isSelecting = false
        },
        { once: true }
      )
      this.$refs.uploader.click()
    },

    onFileChanged(e) {
      let file = e.target.files[0]
      if (!file) {
        return
      }
      if (file.size > 1024 * 1024) {
        let profileImage = this.profileImage
        profileImage.errorMessage = 'Error: File size must be < 1MB'
        profileImage.showError = true
        e.preventDefault()
      } else {
        this.profileImage.showSuccess = true
        this.profileImage.file = file
      }
      this.profileImage.isUploading = false
    },

    async setProfileImage() {
      if (!this.profileImage.file) {
        return Promise.resolve()
      }
      const imageRef = firebase.storage
        .ref()
        .child('profile_images/' + this.uid)
      return imageRef
        .put(this.profileImage.file, {
          contentType: this.profileImage.file.type,
        })
        .then(() => imageRef.getDownloadURL())
        .then((profileImageUrl) =>
          firebase.usersCollection.doc(this.uid).update({ profileImageUrl })
        )
    },
  },
}
</script>

<style scoped></style>
